import React, {
  useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import {
  getArticleBlocksRequest, getArticlesRequest, updateArticle,
  updateArticleBlock,
} from '../../store/actions/articles';
import MyLoading from '../../components/MyLoading';
import MyButton from '../../components/MyButton';
import ArticleBlockListItem from './components/ArticleBlocksItem';
import Api from '../../Api';
import sendRequest from '../../helpers/sendRequest';
import ArticlesModal from './components/ArticlesModal';
import { ARTICLES_MODAL_STATES } from './constants';
import MyToggle from '../../components/MyToggle';
import SingleArticleCard from './components/SingleArticleCard';
import IconButton from '../../components/IconButton';
import DeleteIcon from '../../assets/formIcons/delete.svg';
import { ARTICLES_PAGE_STATES } from '../ArticlesOld/constants';
import ViewIcon from '../../assets/formIcons/viewArticle.svg';
import EditIcon from '../../assets/formIcons/editArticle.svg';
import ArticlesEditBlock from './components/ArticlesEditBlock';
import useInputState from '../../helpers/hooks/useInputState';
import { HTML_TAGS } from '../../helpers/constants';
import CopyTag from '../../components/CopyTag';
import SaveIcon from '../../assets/formIcons/save.svg';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import getFormData from '../../helpers/getFormData';
import ArticlesViewBlock from './components/ArticlesViewBlock';
import checkIfHtmlTextHasJs from '../../helpers/checkIfHtmlTextHasJs';

function Articles() {
  const dispatch = useDispatch();
  const {
    articleBlocks, articles, gettingLoading, error,
  } = useSelector((store) => store.articles);

  const { systemCountry } = useSelector((store) => store.account);

  const [selectedArticleBlock, setSelectedArticleBlock] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [modalState, setModalState] = useState('');
  const [articleBlockToInteract, setArticleBlockToInteract] = useState(null);

  const [articleBlocksSorts, setArticleBlocksSorts] = useState({});
  const [articleBlocksSortsInit, setArticleBlocksSortsInit] = useState({});
  const [articleBlocksSortsLoading, setArticleBlocksSortsLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [innerHtml, setInnerHtml] = useInputState('');

  const onArticleBlockClick = (item) => {
    if (item._id === selectedArticleBlock?._id) return;

    setSelectedArticle(null);
    setSelectedArticleBlock(item);
    dispatch(getArticlesRequest({ article_block_id: item._id, country_id: systemCountry._id }));
  };

  const onArticleClick = (item) => {
    if (item._id === selectedArticle?._id) return;

    setSelectedArticle(item);
    // dispatch(getArticlesRequest({ article_block_id: item._id, country_id: systemCountry._id }));
  };

  const onAddArticleBlockClick = () => {
    setModalState(ARTICLES_MODAL_STATES.newArticleBlock);
  };

  const onAddArticleClick = () => {
    setModalState(ARTICLES_MODAL_STATES.newArticle);
  };

  const onRenameArticleBlockClick = (item) => {
    setArticleBlockToInteract(item);
    setModalState(ARTICLES_MODAL_STATES.renameArticleBlock);
  };
  const onDeleteArticleBlockClick = (item) => {
    setArticleBlockToInteract(item);
    setModalState(ARTICLES_MODAL_STATES.deleteArticleBlock);
  };

  const handleArticleBlockSort = (id) => (ev) => {
    setArticleBlocksSorts((prev) => ({ ...prev, [id]: ev.target.value }));
  };

  const onSaveArticleBlockSortsClick = async () => {
    const list = Object.keys(articleBlocksSorts)
      .filter((key) => articleBlocksSorts[key] !== articleBlocksSortsInit[key])
      .map((key) => ({ articleBlockId: key, sort: articleBlocksSorts[key] || null }));

    const data = await sendRequest({
      request: Api.editSortArticleBlocks,
      payload: { list, country_id: systemCountry?._id },
      setLoading: setArticleBlocksSortsLoading,
    });

    if (data?.status === 'ok') {
      dispatch(getArticleBlocksRequest({ country_id: systemCountry?._id }));
    }
  };

  const onToggleClick = async (item) => {
    await saveArticle([['is_visible', !item.is_visible]], item._id, false);
  };

  const onSaveInnerHtmlClick = async () => {
    if (checkIfHtmlTextHasJs(innerHtml, true)) {
      return;
    }
    await saveArticle([['inner_html', innerHtml]], null, true);
  };

  const saveArticle = async (list, articleId, shouldSetSelectedArticle) => {
    const formData = getFormData({ list });

    const data = await sendRequest({
      request: Api.editArticle,
      payload: { formData, id: articleId || selectedArticle._id, country_id: systemCountry?._id },
    });

    if (data?.status === 'ok') {
      dispatch(updateArticle(data.article));
      if (shouldSetSelectedArticle) setSelectedArticle(data.article);
      setIsEditing(false);
      setInnerHtml('', true);
    }
  };

  const onEditArticleClick = () => {
    setInnerHtml(selectedArticle.inner_html);
    setIsEditing(true);
  };

  const onCancelArticleEditingClick = () => {
    setIsEditing(false);
    setInnerHtml('', true);
  };

  const onDeleteArticleClick = () => {
    setModalState(ARTICLES_MODAL_STATES.deleteArticle);
  };

  useEffect(() => {
    if (articleBlocks?.length) {
      const sorts = articleBlocks.reduce((acc, i) => ({ ...acc, [i._id]: String(i.sort || '') }), {});
      setArticleBlocksSorts(sorts);
      setArticleBlocksSortsInit(sorts);
    }
  }, [articleBlocks]);

  useEffect(() => {
    if (systemCountry?._id && articleBlocks[0]?.country_id !== systemCountry._id) {
      dispatch(getArticleBlocksRequest({ country_id: systemCountry._id }));
    }
  }, [systemCountry?._id]);

  if (!systemCountry?._id) return null;

  return (
    <div>
      {!isEditing && (
        <PageTitle
          text="Статьи"
          RightComponent={<MyLoading height={30} width={30} strokeWidth={4} visible={gettingLoading} />}
        />
      )}
      {isEditing ? (
        <div className={classNames(styles.main, styles.mainEditing)}>
          <ArticlesEditBlock
            innerHtml={innerHtml}
            setInnerHtml={setInnerHtml}
          />
          <div className={styles.tagsList}>
            {HTML_TAGS.map((tag, index) => <CopyTag key={index} {...tag} />)}
            <p className={styles.tagsDescr}>В теге PRE учитывается каждый Энтер. Даже если надо в PRE вставить B, то сам тег B должен быть без Энтеров и всё на одной строчке (если, конечно, в этом нет необходимости). Игнорируются только самый первый и самый последний Энтеры рядом с тегом PRE. Это сделано для лучшей читаемости кода, все остальные же будут добавлять вертикальный отступ</p>
          </div>
          <div className={styles.viewBlock}>
            <div className={classNames(styles.articleCardButtons, styles.viewBlockButtons)}>
              <IconButton
                svgPath={CancelEditingIcon}
                wrapperClassName={styles.articleCardButton}
                tooltipText="Отменить"
                onClick={onCancelArticleEditingClick}
              />
              <IconButton
                svgPath={SaveIcon}
                wrapperClassName={styles.articleCardButton}
                tooltipText="Сохранить"
                onClick={onSaveInnerHtmlClick}
              />
            </div>
            <ArticlesViewBlock innerHtml={innerHtml} />
          </div>
        </div>
      ) : (
        <div className={styles.main}>
          <div className={styles.block}>
            <p className={styles.blockTitle}>Категории</p>
            <MyButton
              secondary
              text="Добавить"
              onClick={onAddArticleBlockClick}
              className={styles.blockButton}
            />
            <div className={styles.list}>
              {articleBlocks?.length ? (
                articleBlocks.map((item) => (
                  <ArticleBlockListItem
                    key={item._id}
                    item={item}
                    textToShow={item.name}
                    clickable
                    sort={articleBlocksSorts[item._id]}
                    handleSort={handleArticleBlockSort(item._id)}
                    selected={item._id === selectedArticleBlock?._id}
                    onItemClick={() => onArticleBlockClick(item)}
                    onRenameClick={() => onRenameArticleBlockClick(item)}
                    onDeleteClick={() => onDeleteArticleBlockClick(item)}
                  />
                ))
              ) : <p className={styles.listEmptyText}>Пусто.</p>}
            </div>
            {JSON.stringify(articleBlocksSorts) !== JSON.stringify(articleBlocksSortsInit) && (
              <MyButton
                text="Сохранить сортировку"
                className={styles.listBottomButton}
                onClick={onSaveArticleBlockSortsClick}
                loading={articleBlocksSortsLoading}
              />
            )}
          </div>

          {selectedArticleBlock && (
            <div className={styles.block}>
              <p className={styles.blockTitle}>Статьи</p>
              <MyButton
                secondary
                text="Добавить"
                onClick={onAddArticleClick}
                className={styles.blockButton}
              />
              <div className={styles.list}>
                {articles?.length ? (
                  articles.map((item) => (
                    <div
                      className={classNames(styles.article, { [styles.articleActive]: item._id === selectedArticle?._id })}
                      key={item._id}
                      onClick={() => onArticleClick(item)}
                    >
                      <img src={Api.url + item.photo_url} alt="file" className={styles.articlePhoto} />
                      <div className={styles.articleRight}>
                        <div className={styles.articleHeader}>
                          <p className={styles.articleTitle}>{item.name}</p>
                          <p className={styles.articleSort}>
                            №
                            {item.sort}
                          </p>
                        </div>
                        <div className={styles.articleFooter}>
                          <p className={styles.articleDate}>{moment(item.updatedAt).format('DD.MM.YYYY')}</p>
                          <MyToggle
                            value={item.is_visible}
                            onClick={() => onToggleClick(item)}
                            isDisabled={!item.inner_html}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : <p className={styles.listEmptyText}>Пусто.</p>}
              </div>
            </div>
          )}
          {selectedArticle && (
            <div className={classNames(styles.block, styles.blockLast)}>
              <SingleArticleCard
                selectedArticle={selectedArticle}
              />

              <div className={styles.articleCardButtons}>
                <IconButton
                  svgPath={DeleteIcon}
                  wrapperClassName={styles.articleCardButton}
                  tooltipText="Удалить"
                  onClick={onDeleteArticleClick}
                />
                <IconButton
                  svgPath={EditIcon}
                  wrapperClassName={styles.articleCardButton}
                  tooltipText="Редактировать"
                  onClick={onEditArticleClick}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <ArticlesModal
        modalState={modalState}
        setModalState={setModalState}
        articleBlockToInteract={articleBlockToInteract}
        selectedArticleBlock={selectedArticleBlock}
        setSelectedArticleBlock={setSelectedArticleBlock}
        selectedArticle={selectedArticle}
        setSelectedArticle={setSelectedArticle}
      />
    </div>
  );
}

export default Articles;
